import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRequest } from "ahooks";
import { Form, Select, Input, Button, Space } from "antd";
import BasePopup from "./BasePopup";
import type { RoleItem } from "@/pages/Home/index.d";
import Api from "@/service";
import { useGlobalStore } from "@/stores";
import { Message, Loading } from "@/components";

import "./index.scss";

interface SecretCodeLoginPopupProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  showButton?: boolean;
}

const SecretCodeLoginPopup: React.FC<SecretCodeLoginPopupProps> = ({
  open,
  onClose,
  onSuccess,
  showButton = true,
}) => {
  const { t } = useTranslation();
  const { login } = useGlobalStore();
  const [form] = Form.useForm();

  // 步骤状态管理
  const [currentStep, setCurrentStep] = useState<
    "secretCode" | "server" | "role"
  >("secretCode");

  // 获取服务器列表 - 只在需要显示服务器选择时才请求
  const {
    data: serverList = [],
    loading: serverLoading,
    run: loadServerList,
  } = useRequest(
    async () => {
      const res = await Api.secretCodeServerList();
      return res.data.list || [];
    },
    {
      manual: true,
    }
  );

  const {
    data: roleList = [],
    loading: roleLoading,
    run: loadRoleList,
  } = useRequest(
    async (secret_code: string, server_id: string) => {
      const res = await Api.queryRoleList(secret_code, server_id);
      return res.data.list || [];
    },
    {
      manual: true,
    }
  );

  // 神秘代码登录逻辑
  const { run: secretCodeLogin, loading: loginLoading } = useRequest(
    async (values: {
      secretCode: string;
      serverId: string;
      roleId: string;
    }) => {
      const secretCodeLogin = await Api.secretCodeLogin(
        values.secretCode,
        values.serverId,
        values.roleId
      );
      return secretCodeLogin.data;
    },
    {
      manual: true,
      onSuccess: (data: { token: string }) => {
        Message.success({ content: t("loginSuccess") });
        login(data.token);
        onSuccess();
        onClose();
      },
    }
  );

  const handleConfirm = async () => {
    try {
      // 验证表单并获取所有字段值
      const values = await form.validateFields();
      console.log("表单数据:", values);

      // 这里可以传递表单数据给登录方法
      secretCodeLogin(values);
    } catch (error) {
      console.error("表单验证失败:", error);
    }
  };

  const handleClose = () => {
    // 重置表单状态
    form.resetFields();
    setCurrentStep("secretCode");
    onClose();
  };

  // 处理服务器选择变化
  const handleServerChange = (serverId: string) => {
    // 清空角色选择
    form.setFieldValue("roleId", undefined);

    if (serverId) {
      // 同时进入角色选择步骤并加载角色列表
      setCurrentStep("role");
      loadRoleList(form.getFieldValue("secretCode"), serverId);
    }
  };

  // 处理神秘代码输入变化
  const handleSecretCodeChange = () => {
    // 当神秘代码改变时，清空服务器和角色选择，并重置步骤
    form.setFieldValue("serverId", undefined);
    form.setFieldValue("roleId", undefined);
    setCurrentStep("secretCode");
  };

  const onVerifySecretCode = async () => {
    try {
      // 获取神秘代码
      const secretCode = form.getFieldValue("secretCode");
      console.log("获取到的神秘代码:", secretCode);

      if (!secretCode || !secretCode.trim()) {
        // 这里可以添加错误提示
        Message.error({ content: t("S_CodeError") });
        return;
      }
      const res = await Api.verifySecretCode(secretCode);

      if (res.data.exist) {
        // 清空服务器和角色选择
        form.setFieldValue("serverId", undefined);
        form.setFieldValue("roleId", undefined);

        setCurrentStep("server");
        loadServerList();
      } else {
        Message.error({ content: t("S_CodeError") });
      }
    } catch (error) {
      console.error("表单验证失败:", error);
    }
  };

  const isLoading = serverLoading || loginLoading;

  const formContent = (
    <Form
      form={form}
      layout='vertical'
      onValuesChange={(changedFields, allFields) => {
        console.log("changedFields", changedFields);
        console.log("allFields", allFields);
      }}>
      {/* 步骤1: 神秘代码验证 */}
      <Form.Item label={t("S_Code")} name='secretCode'>
        <Space.Compact style={{ width: "100%" }}>
          <Input
            onChange={handleSecretCodeChange}
            onPressEnter={onVerifySecretCode}
          />
          <Button
            type='primary'
            className='verify-button'
            onClick={onVerifySecretCode}>
            {t("Verify")}
          </Button>
        </Space.Compact>
      </Form.Item>

      {/* 步骤2: 服务器选择 - 神秘代码验证成功后显示，选择服务器后仍然显示 */}
      {(currentStep === "server" || currentStep === "role") && (
        <Form.Item label={t("server")} name='serverId'>
          <Select
            placeholder={t("pleaseSelect")}
            loading={serverLoading}
            showSearch
            onChange={handleServerChange}>
            {serverList.map((server) => (
              <Select.Option value={server.server_id} key={server.server_id}>
                {server.server_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}

      {/* 步骤3: 角色选择 - 选择服务器后显示 */}
      {currentStep === "role" && roleList?.length >= 1 && (
        <Form.Item label={t("text_roleId")} name='roleId'>
          <Select placeholder={t("pleaseSelect")} loading={roleLoading}>
            {roleList.map((role: RoleItem) => (
              <Select.Option value={role.role_id} key={role.role_id}>
                {role.role_info}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
    </Form>
  );

  const footer = (
    <Button
      type='primary'
      onClick={handleConfirm}
      loading={isLoading}
      disabled={currentStep !== "role"}>
      {t("confirm")}
    </Button>
  );

  return (
    <Loading spinning={false}>
      <BasePopup
        open={open}
        onClose={handleClose}
        title={t("validateAccount")}
        footer={footer}
        showButton={showButton}>
        {formContent}
      </BasePopup>
    </Loading>
  );
};

export default SecretCodeLoginPopup;
